import type { Breadcrumb, DocumentItem, ExplorerView, NormalizeWarning, SortState } from './model.js';
import type { DocumentMapper } from './mapper.js';
import type { DocumentIndex } from './normalize.js';
import { emptyIndex, normalize } from './normalize.js';
import { getBreadcrumbs, getChildren, getParentId } from './tree.js';
import { searchDocuments, type SearchOptions } from './search.js';
import {
  resetColumnWidth,
  setColumnWidth,
  type ColumnWidthBounds,
  type ColumnWidths,
} from './columnSizing.js';
import { DEFAULT_SORT, sortDocuments, toggleSort } from './sort.js';
import {
  DEFAULT_SELECTION_CONFIG,
  EMPTY_SELECTION,
  clearSelection,
  selectAll,
  selectOnly,
  selectRange,
  toggleSelection,
  type SelectionConfig,
  type SelectionState,
} from './selection.js';
import { getDescendants } from './tree.js';

export type ExplorerStatus = 'idle' | 'loading' | 'error';

export interface ExplorerState {
  cwd: string | null;
  query: string;
  sort: SortState;
  view: ExplorerView;
  selection: SelectionState;
  status: ExplorerStatus;
  error?: unknown;
  /** Pixel overrides from column resizing; absent keys use their declared track. */
  columnWidths: ColumnWidths;
}

/** The memoized, render-ready view of the data. */
export interface ExplorerSnapshot {
  state: ExplorerState;
  index: DocumentIndex;
  /** Rows to render, already filtered and sorted. */
  items: DocumentItem[];
  breadcrumbs: Breadcrumb[];
  selectedItems: DocumentItem[];
  warnings: NormalizeWarning[];
  /** True when there is data but the current filter/folder shows nothing. */
  isEmpty: boolean;
  /** True when the query is non-empty (search results, not a folder listing). */
  isSearching: boolean;
  totalCount: number;
}

export interface ExplorerEvents {
  onFolderOpen?: (folder: DocumentItem | null) => void;
  onDocumentOpen?: (item: DocumentItem) => void;
  onSelectionChange?: (items: DocumentItem[]) => void;
  onSearch?: (query: string) => void;
  onSort?: (sort: SortState) => void;
  onViewChange?: (view: ExplorerView) => void;
  /**
   * Fires once per drag, on release — the right moment to persist widths.
   * `width` is `null` when the column was reset to its declared track, which is
   * a different thing from being dragged to zero.
   */
  onColumnResize?: (key: string, width: number | null, widths: ColumnWidths) => void;
}

export interface ExplorerStoreConfig<T = unknown> {
  data?: readonly T[];
  mapper?: DocumentMapper<T>;
  rootLabel?: string;
  initialCwd?: string | null;
  view?: ExplorerView;
  sort?: Partial<SortState>;
  selection?: Partial<SelectionConfig>;
  /** Starting widths, e.g. restored from a previous session. */
  columnWidths?: ColumnWidths;
  search?: SearchOptions & {
    /** Search the whole tree rather than only the current folder. */
    scope?: 'folder' | 'global';
  };
  events?: ExplorerEvents;
}

export interface ExplorerStore<T = unknown> {
  getState(): ExplorerState;
  getSnapshot(): ExplorerSnapshot;
  subscribe(listener: () => void): () => void;
  getSelectionConfig(): SelectionConfig;
  setEvents(events: ExplorerEvents | undefined): void;
  /** Push changed host props (labels, search options) into the store. */
  configure(patch: Pick<ExplorerStoreConfig<T>, 'rootLabel' | 'search'>): void;
  actions: {
    setData(data: readonly T[] | undefined, mapper?: DocumentMapper<T>): void;
    setStatus(status: ExplorerStatus, error?: unknown): void;
    openFolder(id: string | null): void;
    navigateUp(): void;
    open(item: DocumentItem): void;
    setQuery(query: string): void;
    setSort(sort: SortState | string): void;
    setView(view: ExplorerView): void;
    setColumnWidth(key: string, width: number, bounds?: ColumnWidthBounds): void;
    resetColumnWidth(key: string): void;
    setColumnWidths(widths: ColumnWidths): void;
    /** Report the finished drag; state is already current. */
    commitColumnResize(key: string): void;
    setSelectionConfig(config: Partial<SelectionConfig>): void;
    toggleSelect(item: DocumentItem): void;
    selectOnly(item: DocumentItem): void;
    selectRange(item: DocumentItem): void;
    selectAll(): void;
    clearSelection(): void;
  };
}

/**
 * The engine. Everything the explorer *does* lives here, framework-free, so
 * the React and Angular packages are thin subscribers and cannot drift apart.
 */
export function createExplorerStore<T = unknown>(config: ExplorerStoreConfig<T> = {}): ExplorerStore<T> {
  const listeners = new Set<() => void>();
  let events = config.events;
  let rootLabel = config.rootLabel ?? 'Home';
  let searchConfig = config.search ?? {};
  let selectionConfig: SelectionConfig = { ...DEFAULT_SELECTION_CONFIG, ...config.selection };

  let normalized = normalize(config.data, { mapper: config.mapper });

  let state: ExplorerState = {
    cwd: config.initialCwd ?? null,
    query: '',
    sort: { ...DEFAULT_SORT, ...config.sort },
    view: config.view ?? 'list',
    selection: EMPTY_SELECTION,
    status: 'idle',
    columnWidths: config.columnWidths ?? {},
  };

  // --- snapshot memoization -------------------------------------------
  // Recomputing filter -> sort on every subscriber read would make large
  // datasets crawl, and would break useSyncExternalStore's identity check.
  let snapshot: ExplorerSnapshot | null = null;
  let snapshotKey = '';

  function keyOf(): string {
    return [
      normalized,
      state.cwd,
      state.query,
      state.sort.key,
      state.sort.direction,
      state.sort.foldersFirst,
      state.view,
      state.selection,
      state.status,
      state.columnWidths,
    ]
      .map((part) => (typeof part === 'object' && part !== null ? objectId(part) : String(part)))
      .join('|');
  }

  // Identity tokens for the two object-valued inputs, so the key is a string
  // but still changes exactly when those objects are replaced.
  const ids = new WeakMap<object, number>();
  let nextId = 1;
  function objectId(o: object): string {
    let id = ids.get(o);
    if (id === undefined) {
      id = nextId++;
      ids.set(o, id);
    }
    return `#${id}`;
  }

  function build(): ExplorerSnapshot {
    const index = normalized.index;
    const isSearching = state.query.trim() !== '';
    const scope = searchConfig.scope ?? 'folder';

    const source = isSearching && scope === 'global' ? getDescendants(index, null) : getChildren(index, state.cwd);

    const filtered = isSearching ? searchDocuments(source, state.query, searchConfig) : source;
    const items = sortDocuments(filtered, state.sort);

    const selectedItems: DocumentItem[] = [];
    for (const id of state.selection.ids) {
      const item = index.byId.get(id);
      if (item) selectedItems.push(item);
    }

    return {
      state,
      index,
      items,
      breadcrumbs: getBreadcrumbs(index, state.cwd, rootLabel),
      selectedItems,
      warnings: normalized.warnings,
      isEmpty: items.length === 0,
      isSearching,
      totalCount: normalized.items.length,
    };
  }

  function getSnapshot(): ExplorerSnapshot {
    const key = keyOf();
    if (snapshot === null || key !== snapshotKey) {
      snapshot = build();
      snapshotKey = key;
    }
    return snapshot;
  }

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function update(patch: Partial<ExplorerState>): void {
    state = { ...state, ...patch };
    emit();
  }

  function notifySelection(): void {
    if (!events?.onSelectionChange) return;
    events.onSelectionChange(getSnapshot().selectedItems);
  }

  const actions: ExplorerStore<T>['actions'] = {
    setData(data, mapper) {
      normalized = normalize(data, { mapper: mapper ?? config.mapper });
      // Drop selected ids that no longer exist, and leave a folder that is gone.
      const keptIds = new Set([...state.selection.ids].filter((id) => normalized.index.byId.has(id)));
      const cwd = state.cwd !== null && !normalized.index.byId.has(state.cwd) ? null : state.cwd;
      state = {
        ...state,
        cwd,
        selection: {
          ids: keptIds,
          anchorId: state.selection.anchorId && keptIds.has(state.selection.anchorId) ? state.selection.anchorId : null,
        },
      };
      emit();
    },

    setStatus(status, error) {
      update({ status, error });
    },

    openFolder(id) {
      if (state.cwd === id && state.query === '') return;
      // Leaving a folder clears the query: a stale filter in a new folder is
      // the single most confusing thing a file browser can do.
      update({ cwd: id, query: '' });
      events?.onFolderOpen?.(id === null ? null : normalized.index.byId.get(id) ?? null);
    },

    navigateUp() {
      const parent = getParentId(normalized.index, state.cwd);
      actions.openFolder(parent);
    },

    open(item) {
      if (item.type === 'folder') actions.openFolder(item.id);
      else events?.onDocumentOpen?.(item);
    },

    setQuery(query) {
      if (state.query === query) return;
      update({ query });
      events?.onSearch?.(query);
    },

    setSort(sort) {
      const next = typeof sort === 'string' ? toggleSort(state.sort, sort) : sort;
      update({ sort: next });
      events?.onSort?.(next);
    },

    setView(view) {
      if (state.view === view) return;
      update({ view });
      events?.onViewChange?.(view);
    },

    setColumnWidth(key, width, bounds) {
      const columnWidths = setColumnWidth(state.columnWidths, key, width, bounds);
      // Identity is preserved when the clamped width is unchanged, so a drag
      // held against the minimum does not re-render on every pointer move.
      if (columnWidths === state.columnWidths) return;
      update({ columnWidths });
    },

    resetColumnWidth(key) {
      const columnWidths = resetColumnWidth(state.columnWidths, key);
      if (columnWidths === state.columnWidths) return;
      update({ columnWidths });
      events?.onColumnResize?.(key, null, columnWidths);
    },

    setColumnWidths(widths) {
      update({ columnWidths: { ...widths } });
    },

    commitColumnResize(key) {
      const width = state.columnWidths[key];
      if (width === undefined) return;
      events?.onColumnResize?.(key, width, state.columnWidths);
    },

    setSelectionConfig(patch) {
      selectionConfig = { ...selectionConfig, ...patch };
      if (!selectionConfig.enabled && state.selection.ids.size > 0) {
        update({ selection: clearSelection() });
        notifySelection();
      }
    },

    toggleSelect(item) {
      const selection = toggleSelection(state.selection, item, selectionConfig);
      if (selection === state.selection) return;
      update({ selection });
      notifySelection();
    },

    selectOnly(item) {
      const selection = selectOnly(state.selection, item, selectionConfig);
      if (selection === state.selection) return;
      update({ selection });
      notifySelection();
    },

    selectRange(item) {
      const selection = selectRange(state.selection, item, getSnapshot().items, selectionConfig);
      if (selection === state.selection) return;
      update({ selection });
      notifySelection();
    },

    selectAll() {
      const selection = selectAll(state.selection, getSnapshot().items, selectionConfig);
      if (selection === state.selection) return;
      update({ selection });
      notifySelection();
    },

    clearSelection() {
      if (state.selection.ids.size === 0) return;
      update({ selection: clearSelection() });
      notifySelection();
    },
  };

  return {
    getState: () => state,
    getSnapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSelectionConfig: () => selectionConfig,
    setEvents(next) {
      events = next;
    },
    configure(patch) {
      let changed = false;
      if (patch.rootLabel !== undefined && patch.rootLabel !== rootLabel) {
        rootLabel = patch.rootLabel;
        changed = true;
      }
      if (patch.search !== undefined && patch.search !== searchConfig) {
        searchConfig = patch.search;
        changed = true;
      }
      // Force the memo to rebuild, since these inputs are not part of the key.
      if (changed) {
        snapshot = null;
        emit();
      }
    },
    actions,
  };
}

export { emptyIndex };
